import { format, isSameDay } from 'date-fns';
import React, { useRef } from 'react';
import { PLAYBOOK_CHANNEL } from '../../hooks/use-playbook-channel';
import { chatFormatDate } from './common';

const UnMemoizedChannelPreviewMessenger = (props) => {
  const {
    active,
    channel,
    displayImage,
    displayTitle,
    lastMessage,
    setActiveChannel,
    unread,
    watchers,
  } = props;

  const isPlaybookChannel = channel.id === PLAYBOOK_CHANNEL;

  const channelPreviewButton = useRef<HTMLButtonElement | null>(null);

  const activeClass = active ? 'bg-primary-200' : '';
  const isUnread = unread && unread >= 1;

  const onSelectChannel = () => {
    if (setActiveChannel) {
      setActiveChannel(channel, watchers);
    }
    if (channelPreviewButton?.current) {
      channelPreviewButton.current.blur();
    }
  };

  const lastMessageText = (() => {
    if (!lastMessage) return '';
    if (lastMessage.text) return lastMessage.text;
    if (lastMessage.attachments?.[0]?.type === 'video') return '👋 Video';
    if (lastMessage.attachments?.[0]?.type === 'image') return '📷 Photo';

    return '';
  })();

  return (
    <div
      className={
        active
          ? 'preview active-preview border-y border-transparent'
          : 'preview inactive-preview border-t border-gray-200'
      }
    >
      <button
        aria-label={`Select Channel: ${displayTitle || ''}`}
        aria-selected={active}
        className={`flex items-center w-full p-3 text-left rounded-lg ${activeClass} gap-2`}
        data-testid="channel-preview-button"
        onClick={onSelectChannel}
        ref={channelPreviewButton}
        role="option"
      >
        {displayImage && (
          <img className="w-10 h-10 rounded-full" src={displayImage} />
        )}

        {!displayImage && (
          <div
            className={`w-10 h-10 rounded-full bg-[#8A8E99] flex items-center justify-center text-white font-semibold`}
          >
            {displayTitle?.[0]}
          </div>
        )}
        <div className="flex flex-col flex-1 justify-center">
          <div className="str-chat__channel-preview-messenger--name flex justify-between">
            <span
              className={`font-black ${
                isPlaybookChannel ? 'text-primary' : ''
              } text-[18px]`}
            >
              {displayTitle}
            </span>
            {isUnread ? (
              <span className="bg-primary rounded-full px-2 text-tiny text-white">
                {unread}
              </span>
            ) : null}
          </div>
          {lastMessage && (
            <div className="flex justify-between w-full text-[14px] text-gray-500">
              <span
                className={`w-32 truncate ${
                  active ? 'text-[#151E34]' : 'text-[#8C8F98]'
                }`}
              >
                {lastMessageText}
              </span>
              <span className="text-[12px]">
                {chatFormatDate(new Date(lastMessage?.created_at))}
              </span>
            </div>
          )}
        </div>
      </button>
    </div>
  );
};

/**
 * Used as preview component for channel item in [ChannelList](#channellist) component.
 * Its best suited for messenger type chat.
 */
export const ChannelPreviewMessenger = React.memo(
  UnMemoizedChannelPreviewMessenger,
) as typeof UnMemoizedChannelPreviewMessenger;
